import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import DocumentCard from '../components/DocumentCard';
import DocumentModal from '../components/DocumentModal';
import ConfirmDialog from '../components/ConfirmDialog';

const CATEGORIES = [
    { value: 'common', label: 'Common' },
    { value: 'salaried', label: 'Salaried' },
    { value: 'selfEmployed', label: 'Self-Employed' },
    { value: 'nri', label: 'NRI' },
    { value: 'property', label: 'Property' }
];

const MortgageLoanDocumentsPage = () => {
    const [documentsList, setDocumentsList] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Documents Required',
        section_subtitle: 'Keep these documents ready for a smooth application process'
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDocument, setEditingDocument] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, documentId: null });
    const [activeCategory, setActiveCategory] = useState('common');

    const LOAN_TYPE = 'mortgage_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [documentsRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/documents/${LOAN_TYPE}`),
                axios.get(`/api/admin/documents/settings/${LOAN_TYPE}`)
            ]);

            setDocumentsList(documentsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load documents data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter documents by active category
    const filteredDocuments = documentsList.filter(doc => doc.category === activeCategory);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingDocument) {
                await axios.put(`/api/admin/documents/${editingDocument.id}`, formData);
                toast.success('Document updated successfully');
            } else {
                await axios.post('/api/admin/documents', formData);
                toast.success('Document created successfully');
            }
            setIsModalOpen(false);
            setEditingDocument(null);
            fetchData();
        } catch (error) {
            console.error('Error saving document:', error);
            toast.error('Failed to save document');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, documentId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/documents/${deleteConfirm.documentId}`);
            toast.success('Document deleted successfully');
            setDeleteConfirm({ isOpen: false, documentId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/documents/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Documents Required</h1>
                    <p className="text-slate-500 mt-1">Manage the required documents for Mortgage Loan</p>
                </div>
                <button
                    onClick={() => {
                        setEditingDocument(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Document
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Documents Required"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Keep these documents ready..."
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Category Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex flex-wrap gap-2 mb-6">
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat.value}
                            onClick={() => setActiveCategory(cat.value)}
                            className={`px-6 py-2 rounded-xl font-semibold transition-all ${activeCategory === cat.value
                                ? 'bg-primary-600 text-white shadow-lg'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Documents Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-32 shadow-sm animate-pulse border border-slate-100"></div>
                        ))}
                    </div>
                ) : filteredDocuments.length === 0 ? (
                    <div className="bg-slate-50 rounded-2xl p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <DocumentTextIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2">No documents in this category</h3>
                            <p className="text-slate-500 mb-6">Add documents for the {CATEGORIES.find(c => c.value === activeCategory)?.label} category.</p>
                            <button
                                onClick={() => {
                                    setEditingDocument(null);
                                    setIsModalOpen(true);
                                }}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                            >
                                Add First Document
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredDocuments.map((document) => (
                            <DocumentCard
                                key={document.id}
                                document={document}
                                onEdit={(doc) => {
                                    setEditingDocument(doc);
                                    setIsModalOpen(true);
                                }}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            <DocumentModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingDocument(null);
                }}
                onSubmit={handleSubmit}
                document={editingDocument}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, documentId: null })}
                onConfirm={confirmDelete}
                title="Delete Document"
                message="Are you sure you want to delete this document? This action cannot be undone."
            />
        </div>
    );
};

export default MortgageLoanDocumentsPage;
